import {
  ConnectionListResult,
  GridSortProps,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import { DashboardsDisplayGridRowDataFragment } from '../../../gen-src/queries/DashboardsDisplayGridRowDataFragment';
import {
  DownstreamDashboardsFilteredByFieldQuery,
  DownstreamDashboardsFilteredByFieldQueryVariables,
} from '../../../gen-src/queries/DownstreamDashboardsFilteredByFieldQuery';
import {
  WorkbookDownstreamDashboardsQuery,
  WorkbookDownstreamDashboardsQueryVariables,
} from '../../../gen-src/queries/WorkbookDownstreamDashboardsQuery';
import { DashboardSortOrder } from '../../../gen-src/queries/globalTypes';
import { DownstreamDashboardsFilteredByFieldQueryGql } from '../../queries/DownstreamDashboardsFilteredByFieldQueryGql';
import { WorkbookDownstreamDashboardsQueryGql } from '../../queries/WorkbookDownstreamDashboardsQueryGql';
import { DashboardsDisplayGrid, DashboardsDisplayGridRowData, getRowDataForDashboard } from '../sharedDisplayGrid/DashboardsDisplayGrid';
import { DashboardsValidSortFields } from '../sorting/sortProviders/DashboardsGridSortProvider';

type QueryVariables = WorkbookDownstreamDashboardsQueryVariables;
type QueryResults = WorkbookDownstreamDashboardsQuery;
type QueryResultListNode = DashboardsDisplayGridRowDataFragment;
type GridRowData = DashboardsDisplayGridRowData;

type QueryVariablesWithFilter = DownstreamDashboardsFilteredByFieldQueryVariables;
type QueryResultsWithFilter = DownstreamDashboardsFilteredByFieldQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.workbooks && resultData.workbooks[0] && resultData.workbooks[0].dashboardsConnection;
}

function selectConnectionListResultForFilteredQuery(resultData: QueryResultsWithFilter):
  ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.fieldsConnection && resultData.fieldsConnection.nodes &&
    resultData.fieldsConnection.nodes[0] && resultData.fieldsConnection.nodes[0].downstreamDashboardsConnection;
}

export type WorkbookDownstreamDashboardsQueryGridProps =
  GridSortProps<DashboardsValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly filteredFieldId: string | null;
    readonly rowCount: number | null;
  };

export function WorkbookDownstreamDashboardsQueryGrid(props: WorkbookDownstreamDashboardsQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamDashboardsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredDashboardsLineageGridMessage();

  return props.filteredFieldId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={DownstreamDashboardsFilteredByFieldQueryGql}
      partialQueryVariables={{ selectedFieldId: props.filteredFieldId, orderBy: props.orderBy as DashboardSortOrder }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForDashboard}
    >
      {({ rowStore }) => <DashboardsDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyFilteredGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  ) : (
      // Query without Selection
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={WorkbookDownstreamDashboardsQueryGql}
        partialQueryVariables={{ workbookVizportalUrlId: props.vizportalUrlId, orderBy: props.orderBy as DashboardSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForDashboard}
      >
        {({ rowStore }) => <DashboardsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    );
}
